import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import { useGlobalCoupon } from "~/contexts/globalcoupon";
import { useCartWithHooks } from "~/hooks/use-cart";
import useLoyaltyRewards, { LOYALTY_REWARDS_QUERY_KEY } from "~/hooks/use-loyalty-rewards";
import useRewardRequirementHint from "~/hooks/use-reward-requirement-hint";
import type { LoyaltyReward } from "~/shared/loyalty/loyalty-rewards.types";
import {
  findRewardDiscountLineId,
  getApplicableCouponCode,
  getRewardCta,
  getRewardLabel,
  hasAppliedCouponOnCart,
  isRewardAppliedOnCart,
  isRewardAvailable,
  isRewardPendingOnCart,
} from "~/shared/loyalty/loyalty-rewards.utils";
import AppRoutes from "~/utils/app-routes";
import appRoutes from "~/utils/app-routes";

import Button from "../common/button";
import LinkWrapper from "../common/link-wrapper";
import styles from "./rewards-card.module.scss";

export default function RewardsCard({ onlyAvailable = false }: { onlyAvailable?: boolean } = {}) {
  const t = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { cart, cancelDiscountOrderLine } = useCartWithHooks();
  const { applyCoupon } = useGlobalCoupon();
  const { data } = useLoyaltyRewards({ onlyAvailable });
  const requirementHint = useRewardRequirementHint();
  const [busyRewardKey, setBusyRewardKey] = useState<string>();
  const [feedback, setFeedback] = useState<{ rewardKey: string; message: string }>();
  const isMyAccountLoyaltyPage = useMemo(() => router.asPath === appRoutes.LOYALTY + "/", [router.asPath]);

  const rewards = data ?? [];

  if (rewards.length === 0) {
    return null;
  }

  const lines = cart?.ShoppingCart?.Lines ?? [];
  const hasOrder = !!cart?.ShoppingCart?.ID;
  const hasCartProducts = lines.some((line) => line.Product);
  // One discounting coupon at a time, whatever applied it - a code typed in the cart included.
  const hasAppliedCoupon = hasAppliedCouponOnCart(lines);

  const onApply = async (rewardKey: string, reward: LoyaltyReward) => {
    const code = getApplicableCouponCode(reward);
    if (!code || busyRewardKey) {
      return;
    }
    // One coupon at a time: the CTA is already blocked, this also covers a stale render.
    if (hasAppliedCoupon) {
      setFeedback({ rewardKey, message: t("generic.myaccount.dashboard.rewardsOneCouponAtATime") });
      return;
    }
    setFeedback(undefined);
    setBusyRewardKey(rewardKey);
    try {
      const response = await applyCoupon(code);
      if (response && !response.DiscountIsApplied) {
        setFeedback({ rewardKey, message: t("error.invalid_coupon_combination") });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: [LOYALTY_REWARDS_QUERY_KEY] });
      router.push(AppRoutes.CART);
    } catch {
    } finally {
      setBusyRewardKey(undefined);
    }
  };

  const onRemove = async (rewardKey: string, reward: LoyaltyReward) => {
    const lineId = findRewardDiscountLineId(lines, reward);
    if (!lineId || busyRewardKey) {
      return;
    }
    setFeedback(undefined);
    setBusyRewardKey(rewardKey);
    try {
      await cancelDiscountOrderLine({ payload: { OrderLineID: lineId } });
      await queryClient.invalidateQueries({ queryKey: [LOYALTY_REWARDS_QUERY_KEY] });
    } catch {
      setFeedback({ rewardKey, message: t("error.checkout.generic") });
    } finally {
      setBusyRewardKey(undefined);
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t("generic.myaccount.dashboard.rewardsTitle")}</h2>
        {!isMyAccountLoyaltyPage && (
          <LinkWrapper href={AppRoutes.LOYALTY} className={styles.desktopOnly}>
            {t("generic.myaccount.dashboard.rewardsViewProgramLink")}
          </LinkWrapper>
        )}
      </div>
      <p className={styles.subtitle}>{t("generic.myaccount.dashboard.rewardsSubtitle")}</p>

      <ul className={styles.list}>
        {rewards.map((reward, index) => {
          const rewardKey = `${reward.BackendID ?? "reward"}-${index}`;
          const cta = getRewardCta({
            applied: isRewardAppliedOnCart(lines, reward),
            pending: isRewardPendingOnCart(lines, reward, hasOrder),
            hasCoupons: reward.coupons.length > 0,
            available: isRewardAvailable(reward),
            hasCartProducts,
            hasAppliedCoupon,
          });
          const isRemovable = !!findRewardDiscountLineId(lines, reward);
          const isBusy = busyRewardKey === rewardKey;
          const isApplied = cta === "applied" && isRemovable;
          const isUnavailable = !isRewardAvailable(reward);
          const label = getRewardLabel(reward);
          const description = reward.MarketingDescription || reward.Description;
          const hint =
            cta === "pending" || cta === "disabled"
              ? requirementHint(reward) ??
                (cta === "pending" ? t("generic.myaccount.dashboard.rewardsPendingHint") : undefined)
              : undefined;

          return (
            <li
              key={rewardKey}
              className={classNames(styles.item, {
                [styles.itemApplied]: isApplied,
                [styles.itemUnavailable]: !isApplied && isUnavailable,
              })}
            >
              <div className={styles.itemHead}>
                <span className={styles.itemTitle}>{label}</span>
                {cta === "noCoupon" && (
                  <span className={styles.noCoupon}>{t("generic.myaccount.dashboard.withoutCoupon")}</span>
                )}
                {cta === "applied" && (
                  <button
                    type="button"
                    className={styles.remove}
                    disabled={!!busyRewardKey}
                    onClick={() => onRemove(rewardKey, reward)}
                  >
                    {t("generic.remove")}
                  </button>
                )}
                {/* {cta === "pending" && (
                  <span className={styles.applied}>{t("generic.myaccount.dashboard.rewardsPending")}</span>
                )} */}
                {cta !== "applied" && cta !== "noCoupon" && cta !== "pending" && (
                  <Button
                    type="button"
                    variant="primary"
                    loading={isBusy}
                    disabled={cta !== "apply" || !!busyRewardKey}
                    onClick={() => onApply(rewardKey, reward)}
                  >
                    {t("generic.apply")}
                  </Button>
                )}
              </div>
              {description && description !== label && <p className={styles.itemDescription}>{description}</p>}
              {/* {hint && <p className={styles.emptyBanner}>{hint}</p>} */}
              {cta === "empty" && (
                <p className={styles.emptyBanner}>{t("generic.myaccount.dashboard.rewardsEmptyBanner")}</p>
              )}
              {cta === "blocked" && (
                <p className={styles.emptyBanner}>{t("generic.myaccount.dashboard.rewardsOneCouponAtATime")}</p>
              )}
              {feedback?.rewardKey === rewardKey && <p className={styles.feedback}>{feedback.message}</p>}
            </li>
          );
        })}
      </ul>
      {!isMyAccountLoyaltyPage && (
        <LinkWrapper href={AppRoutes.LOYALTY} className={classNames(styles.linkMobile, styles.mobileOnly)}>
          {t("generic.myaccount.dashboard.rewardsViewProgramLink")}
        </LinkWrapper>
      )}
    </section>
  );
}
